'use client';

import { formatReadableNumber } from '@lfg/core/lib';
import { Modal, type ModalHandle, type ModalProps } from '@lfg/ui/components';
import { IconButton, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { icons } from '@/assets/icons';
import { dividers } from '@/assets/materials';
import type { NodeSchemaType } from '@/global/schemas/node.schema';

import { CoinIcon } from './CoinIcon';
import HexagonalButton from './HexagonalButton';

export interface NodeDetailModalHandle extends ModalHandle {}
export interface NodeDetailModalProps extends Omit<ModalProps, 'children'> {
  item: NodeSchemaType;
  setSelectedNode: React.Dispatch<React.SetStateAction<NodeSchemaType | null>>;
}

const NodeDetailModal = forwardRef<NodeDetailModalHandle, NodeDetailModalProps>(
  ({ item, setSelectedNode, ...rest }, ref) => {
    const modalRef = useRef<ModalHandle>(null);

    const show = useCallback(() => {
      modalRef.current?.show();
    }, [modalRef.current]);

    const hide = useCallback(() => {
      modalRef.current?.hide();
      setSelectedNode(null);
    }, [modalRef.current]);

    useImperativeHandle(ref, () => ({ show, hide }), [show, hide]);

    const openNodeSaleUrl = () => {
      window.open(item.nodeSaleUrl, '_blank');
    };

    return (
      <Modal
        ref={modalRef}
        bgColor={'#060912'}
        padding={'50px 24px 24px'}
        borderRadius={'0px'}
        width={'800px'}
        sx={{ maxHeight: { xs: '95vh', sm: 'unset' }, overflowY: { xs: 'auto', sm: 'unset' } }}
        {...rest}>
        <Stack
          alignItems={{ xs: 'center', sm: 'flex-start' }}
          direction={{ xs: 'column', sm: 'row' }}
          gap={{ xs: 2, sm: 3 }}
          paddingTop={'10px'}
          flexGrow={1}
          position={'relative'}>
          <Stack position={'absolute'} top={-30} right={0} zIndex={1000}>
            <IconButton onClick={hide} sx={{ padding: 0 }}>
              <Image width={24} height={24} src={icons.ic_x} alt='' />
            </IconButton>
          </Stack>
          <Stack
            height={{ xs: '240px', sm: '300px' }}
            width={{ xs: '240px', sm: '300px' }}
            position='relative'
            overflow={'hidden'}>
            <Image
              src={item.asset}
              alt={item.name}
              fill
              sizes='(max-width: 600px) 240px, 300px'
              style={{
                objectFit: 'contain',
                objectPosition: 'center',
              }}
              priority
            />
          </Stack>
          <Stack width={{ xs: '100%', sm: 'calc(100% - 300px)' }} gap={{ xs: 2, sm: 3 }}>
            <Stack gap={{ xs: 2, sm: 3 }}>
              <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} gap={{ xs: 2, sm: 3 }}>
                <Typography variant={'displaySmall'} color={'white'} fontSize={{ xs: '20px', sm: '36px' }}>
                  {item.name}
                </Typography>
                <Stack width={140} display={{ xs: 'none', sm: 'block' }}>
                  <HexagonalButton variant={'danger'} size={'small'} label={`TIER ${item.currentTier}`} />
                </Stack>
                <Stack width={84} display={{ xs: 'block', sm: 'none' }}>
                  <HexagonalButton variant={'danger'} size={'tiny'} label={`TIER ${item.currentTier}`} />
                </Stack>
              </Stack>
              <Stack alignItems={'center'} gap={1} direction={'row'}>
                <Image
                  src={item?.networkImage ?? '/icons/coins/ethereum.svg'}
                  alt={'highlight'}
                  width={15}
                  height={24}
                  className={'w-auto h-6 object-contain'}
                />
                <Typography
                  variant={'bodyMedium'}
                  color={'white'}
                  className={'mix-blend'}
                  fontSize={{ xs: '12px', sm: '16px' }}>
                  {item.nftType ?? 'ERC-1155'} NFT
                </Typography>
              </Stack>
              <Typography
                fontSize={{ xs: '14px', md: '18px' }}
                variant={'bodyLarge'}
                color={'#FFFFFF'}
                className={'mix-blend'}
                sx={{ wordBreak: 'break-all' }}>
                {item.description}
              </Typography>
            </Stack>
            <Stack px={{ xs: 3, sm: 5 }} sx={{ width: '100%' }} justifyContent={'center'}>
              <Image
                src={dividers.divider_1}
                alt={'divider'}
                width={544}
                height={2}
                style={{ width: '100%', height: 'auto' }}
              />
            </Stack>
            <Stack alignItems={'center'} justifyContent={'space-between'} direction={'row'} gap={2}>
              <Stack alignItems={'center'} direction={'row'} gap={{ xs: '12px', sm: 2 }}>
                <Stack alignItems={'center'} justifyContent={'center'} display={{ xs: 'none', sm: 'flex' }}>
                  <CoinIcon tokenImage={item.tokenImage} size={52} />
                </Stack>
                <Stack alignItems={'center'} justifyContent={'center'} display={{ xs: 'flex', sm: 'none' }}>
                  <CoinIcon tokenImage={item.tokenImage} size={24} />
                </Stack>
                <Typography
                  variant={'displaySmall'}
                  fontSize={{ xs: '20px', sm: '36px' }}
                  lineHeight={{ xs: '24px', sm: '48px' }}
                  color={'white'}>
                  {formatReadableNumber(item.price, {
                    minimumFractionDigits: 2,
                    style: 'decimal',
                    preventRounding: true,
                  })}{' '}
                  {(item.token || 'USDC').toUpperCase()}
                </Typography>
              </Stack>
              <Stack width={84} display={{ xs: 'none', sm: 'block' }}>
                <HexagonalButton variant={'secondary'} size={'small'}>
                  <Image
                    width={24}
                    height={24}
                    src={icons.ic_arrow_right}
                    alt={'arrow right'}
                    onClick={openNodeSaleUrl}
                  />
                </HexagonalButton>
              </Stack>
              <Stack width={68} display={{ xs: 'block', sm: 'none' }}>
                <HexagonalButton variant={'secondary'} size={'small'}>
                  <Image
                    width={18}
                    height={18}
                    src={icons.ic_arrow_right}
                    alt={'arrow right'}
                    onClick={openNodeSaleUrl}
                  />
                </HexagonalButton>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Modal>
    );
  },
);

export default NodeDetailModal;
